import { FC, ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { u } from '../common/Units';
import ChevronLeftIcon from "../assets/chevron_left.svg?react";
import ChevronRightIcon from "../assets/chevron_right.svg?react";

interface PaginationProps {
  /** Current active page, starting at 1 */
  currentPage: number;

  /** Total number of items to paginate */
  itemCount: number;

  /** Number of items per page. Defaults to 10 */
  pageSize?: number;

  /** Callback for changing page */
  setCurrentPage: (page: number) => void;
}

type PageItem = number | "...";

const PaginationWrapper = styled.ul`
  margin: ${u(28)} 0 0;
  padding: 0;
  list-style: none;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${u(8)};
`;

/** Round buttons in the block color, like the cards above them. The current page is filled with the accent color */
const PageButton = styled.button`
  min-width: ${u(48)};
  font-variant-numeric: tabular-nums;
  height: ${u(48)};
  padding: 0 ${u(10)};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: ${u(24)};
  background-color: ${p => p.theme.blockColor};
  color: inherit;
  font: inherit;
  font-size: 0.9em;
  cursor: pointer;

  & svg {
    width: ${u(18)};
    height: ${u(18)};
    fill: currentColor;
  }

  &.active {
    background-color: ${p => p.theme.accentColor};
    color: #fff;
    font-weight: 600;
  }

  &:active:not(.active):not(:disabled) {
    background-color: ${p => p.theme.accentColor}50;
  }

  /* Previous/next stay in place on the first and last page, so the numbers do not move */
  &:disabled {
    cursor: default;
    opacity: 0.35;
  }
`;

/** As wide as a button, so the row keeps its width whichever pages are shown */
const Ellipsis = styled.li`
  min-width: ${u(48)};
  text-align: center;
  opacity: 0.7;
  user-select: none;
`;

/** Number of slots (pages and ellipses) shown when there are more pages than fit */
const SLOTS = 7;

/**
 * Builds a list of page numbers with a fixed number of slots, so the buttons do not move when the page changes.
 * The first and last page are always shown, and the current page with its neighbours.
 *
 * Examples with 20 pages:
 * page 2  → [1, 2, 3, 4, 5, "...", 20]
 * page 7  → [1, "...", 6, 7, 8, "...", 20]
 * page 19 → [1, "...", 16, 17, 18, 19, 20]
 */
const getPaginationItems = (currentPage: number, pageCount: number): PageItem[] => {
  const range = (from: number, to: number) => Array.from({ length: to - from + 1 }, (_, i) => from + i);
  if (pageCount <= SLOTS) { return range(1, pageCount); }
  if (currentPage <= 4) { return [...range(1, 5), "...", pageCount]; }
  if (currentPage >= pageCount - 3) { return [1, "...", ...range(pageCount - 4, pageCount)]; }
  return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", pageCount];
};

/**
 * Render a compact pagination component.
 *
 * The component avoids rendering every single page number when there are many
 * pages. Instead, it shows a small window around the current page, plus the
 * first and last page.
 */
const Pagination: FC<PaginationProps> = ({
  currentPage,
  itemCount,
  pageSize = 10,
  setCurrentPage
}) => {
  const { t } = useTranslation();
  const pageCount = Math.ceil(itemCount / pageSize);

  const paginationItems: ReactElement[] = useMemo(() => {
    return getPaginationItems(currentPage, pageCount).map((item, index) => {
      if (item === "...") {
        return <Ellipsis key={`slot-${index}`} aria-hidden="true">…</Ellipsis>;
      }
      return (
        // Keyed by slot, not page: the buttons stay in place and only their numbers change
        <li key={`slot-${index}`}>
          <PageButton className={item === currentPage ? "active" : undefined} aria-current={item === currentPage ? "page" : undefined}
            onClick={() => setCurrentPage(item)}>{item}</PageButton>
        </li>
      );
    });
  }, [currentPage, pageCount, setCurrentPage]);

  if (pageCount <= 1) {
    return null;
  }

  return (
    <PaginationWrapper className="showInPreview">
      <li>
        <PageButton onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage <= 1} aria-label={t("pagination.previous")}>
          <ChevronLeftIcon/>
        </PageButton>
      </li>
      {paginationItems}
      <li>
        <PageButton onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage >= pageCount} aria-label={t("pagination.next")}>
          <ChevronRightIcon/>
        </PageButton>
      </li>
    </PaginationWrapper>
  );
};

export default Pagination;